/**
 * The agent companion — the mascot for SIMS' agent-assisted features.
 *
 * Named for what it is rather than what it will be called: the character is
 * still being chosen between three candidates and has no formal name, so a
 * working codename in identifiers, storage keys and CSS would have to be
 * renamed across the codebase later. The one place it survives is the keyword
 * that summons it, which is a word people type rather than an identifier.
 *
 * The name is provisional and the character is still being chosen between three
 * candidates, so everything here is deliberately swappable: the artwork is a
 * `variant`, the copy is a table, and the timings are constants. Nothing about
 * the appearance mechanic depends on which candidate wins.
 */

/** The three candidates from the design exploration sheet. */
export const AGENT_COMPANION_VARIANTS = [
	"leaf",
	"heart",
	"pathfinder",
] as const;
export type AgentCompanionVariant = (typeof AGENT_COMPANION_VARIANTS)[number];

export const AGENT_COMPANION_EXPRESSIONS = [
	"happy",
	"excited",
	"proud",
	"encouraging",
] as const;
export type AgentCompanionExpression =
	(typeof AGENT_COMPANION_EXPRESSIONS)[number];

/**
 * Option 1, "The Leaf Buddy". The sheet works this one up furthest — it is the
 * only candidate drawn with the full expression set, the pose set and the
 * in-app mock — so it is the default until the team picks. Override per-visit
 * with `?agent-companion=heart` / `?agent-companion=pathfinder` to demo the others.
 */
export const AGENT_COMPANION_DEFAULT_VARIANT: AgentCompanionVariant = "leaf";

/**
 * Why the companion turned up. Drives expression, copy and how long it stays.
 *
 * `focus` is the common one: a brief peek beside the field being filled in.
 * The others are the occasions — arriving, being summoned, finishing something
 * — and those are the ones that get a line of dialogue and a longer beat.
 */
export type AgentCompanionTrigger =
	| "idle"
	| "keyword"
	| "random"
	| "celebrate"
	| "focus";

export interface AgentCompanionCue {
	expression: AgentCompanionExpression;
	/** Short, warm, and never about a specific person — decorative copy only. */
	lines: string[];
}

export const AGENT_COMPANION_CUES: Record<
	AgentCompanionTrigger,
	AgentCompanionCue
> = {
	// "Welcome" pose — a wave when the page has been sitting untouched.
	idle: {
		expression: "happy",
		lines: [
			"Still here if you need me.",
			"Take your time.",
			"Building something good?",
		],
	},
	// Someone typed the name on purpose, so play it up.
	keyword: {
		expression: "excited",
		lines: ["You found me!", "That's my name!", "Hi there."],
	},
	// "Check Progress" — a rare hello on arrival.
	random: {
		expression: "encouraging",
		lines: [
			"Let's configure something.",
			"You got this.",
			"Ready when you are.",
		],
	},
	// "Celebrate Win" — a plan actually saved.
	celebrate: {
		expression: "proud",
		lines: ["Nice work!", "That's a wrap.", "Great job!"],
	},
	/**
	 * Peeking at the field being filled in. No line: at this length nobody
	 * finishes reading one, and a bubble beside an input is in the way in a
	 * manner the character alone is not.
	 */
	focus: { expression: "happy", lines: [""] },
};

/** Quiet page for this long before the idle wave. */
export const AGENT_COMPANION_IDLE_MS = 20_000;
/** How long an appearance with something to say stays on screen. */
export const AGENT_COMPANION_HOLD_MS = 6_000;
/**
 * A glimpse. Long enough to register as a character, short enough that it is
 * gone before it can annoy anyone — which is what lets it happen often.
 */
export const AGENT_COMPANION_GLIMPSE_MS = 2_200;
/**
 * Floor between two *unprompted* appearances of the talking kind. Typing the
 * name always works — an easter egg you cannot summon on demand is just a
 * distraction — and field peeks have their own, much shorter, throttle.
 */
export const AGENT_COMPANION_COOLDOWN_MS = 45_000;
/** Beat after mount before the arrival roll, so it reads as a greeting. */
export const AGENT_COMPANION_RANDOM_DELAY_MS = 4_000;
/** Chance of the arrival greeting. */
export const AGENT_COMPANION_RANDOM_CHANCE = 0.45;
/**
 * Settle time before peeking at a newly focused field, so tabbing through a
 * form does not strobe the character across the page.
 */
export const AGENT_COMPANION_FOCUS_DELAY_MS = 400;
/** Floor between two field peeks. The only thing rationing the common case. */
export const AGENT_COMPANION_FOCUS_THROTTLE_MS = 7_000;
/** How big the companion is drawn when peeking beside a field. */
export const AGENT_COMPANION_PEEK_SIZE = 64;
/** How big it is drawn when it has something to say. */
export const AGENT_COMPANION_FULL_SIZE = 116;

/** Type this anywhere outside a text field. */
/**
 * The word that summons it.
 *
 * The one place the provisional working name survives, deliberately: it is a
 * magic word people type, not an identifier, and "cemeni" is what the team
 * already says out loud. Changing it when a real name is chosen is this one
 * line.
 */
export const AGENT_COMPANION_KEYWORD = "cemeni";

/** `localStorage` opt-out, for anyone who would rather work in peace. */
export const AGENT_COMPANION_OFF_KEY = "agent-companion:off";
/** `sessionStorage` cooldown stamp. */
export const AGENT_COMPANION_LAST_SEEN_KEY = "agent-companion:lastSeen";
